import { assertKitchenResponse } from "./kitchenActions";

const text = (value) => String(value ?? "").trim();
const normalized = (value) => text(value).replace(/\s+/g, " ").toLowerCase();
const inactiveStatuses = new Set(["COMPLETED", "REMOVED", "CANCELLED", "CANCELED", "REJECTED"]);

const sortKeys = ["sortByDue", "sortByAcceptedAt", "sortByPrepTime"];

/** Load every matching page before publishing totals; never show partial counts. */
export const loadKitchenWorkload = async (fetchJobs, filters, isCurrent = () => true) => {
  // An explicit sort from the caller replaces the default created-date order.
  const defaultSort = sortKeys.some((key) => filters?.[key]) ? {} : { sortByCreatedDate: "ASCENDING" };
  const loadStatus = async (status) => {
    const jobs = new Map();
    let page = 1;
    let total = Infinity;
    while (jobs.size < total) {
      if (!isCurrent()) return null;
      const response = await fetchJobs({
        ...filters,
        statuses: [status],
        ...defaultSort,
        page,
        pageSize: 100,
      });
      if (!isCurrent()) return null;
      assertKitchenResponse(response);
      const data = response?.data;
      if (!Array.isArray(data?.results)) {
        throw new Error("Unable to load kitchen workload.");
      }
      const results = data.results;
      const count = Number(data.totalResults);
      if (data.totalResults == null || !Number.isFinite(count) || count < 0) {
        throw new Error("Unable to verify the full kitchen workload.");
      }
      total = count;
      let added = 0;
      results.forEach((job) => {
        if (!job?.jobId) throw new Error("Kitchen order is missing its identifier.");
        if (!jobs.has(job.jobId)) added += 1;
        jobs.set(job.jobId, job);
      });
      if (jobs.size < total && (!results.length || !added)) {
        throw new Error("Some kitchen orders could not be loaded. Please retry.");
      }
      page += 1;
    }
    return [...jobs.values()];
  };
  const [pending, processing] = await Promise.all([
    loadStatus("PENDING"),
    loadStatus("IN_PROGRESS"),
  ]);
  if (!isCurrent() || pending == null || processing == null) return null;
  // An order can move status between the two requests; count it only once.
  const uniqueJobs = new Map(pending.map((job) => [job.jobId, job]));
  processing.forEach((job) => uniqueJobs.set(job.jobId, job));
  return [...uniqueJobs.values()].map(normalizeAcceptedKitchenJob);
};

// The currently supported acceptance API moves a whole job to IN_PROGRESS.
// Some legacy responses leave live task.status at PENDING after that move.
// Preserve terminal task states; dish-only acceptance needs its own API contract.
export const normalizeAcceptedKitchenJob = (job) => job.status !== "IN_PROGRESS" ? job : ({
  ...job,
  tasks: (job.tasks || []).map((task) =>
    task?.status === "PENDING" && !inactiveStatuses.has(task.taskStatus)
      ? { ...task, status: "IN_PROGRESS" } : task),
});

const normalizeAddons = (addons) =>
  (Array.isArray(addons) ? addons : []).map((addon) => ({
    name: text(typeof addon === "string" ? addon : addon?.itemName ?? addon?.name ?? addon?.title ?? addon?.addonName),
    quantity: typeof addon === "object" ? addon?.quantity ?? addon?.qty ?? 1 : 1,
  })).filter((addon) => addon.name);

/** Aggregate dish quantities, retaining per-order preparation instructions. */
export const aggregateKitchenItems = (jobs = [], selectedPrepStations = []) => {
  const groups = new Map();
  const seenTasks = new Set();
  jobs.forEach((job) => {
    if (!["PENDING", "IN_PROGRESS"].includes(job?.status)) return;
    (job.tasks || []).forEach((task, index) => {
      if (!task) return;
      if (inactiveStatuses.has(task.taskStatus) || inactiveStatuses.has(task.status)) return;
      const stationId = task.metadata?.prepStationId;
      if (selectedPrepStations.length && !selectedPrepStations.includes(stationId)) return;
      const status = ["PENDING", "IN_PROGRESS"].includes(task.status) ? task.status : job.status;
      const quantity = Number(task.quantity);
      if (!Number.isFinite(quantity) || quantity <= 0) return;
      const taskKey = JSON.stringify([job.jobId, task.taskId || index]);
      if (seenTasks.has(taskKey)) return;
      seenTasks.add(taskKey);
      const name = text(task.title) || "Unnamed item";
      const variant = text(task.metadata?.variantName ?? task.additionalProperties?.variantName);
      // Consolidate the named dish across order-specific IDs and variants.
      // Keep unnamed products distinct rather than combining unrelated work.
      const key = text(task.title)
        ? JSON.stringify(["dish", normalized(name)])
        : JSON.stringify(["unnamed", text(task.externalId || task.itemId) || taskKey]);
      if (!groups.has(key)) {
        groups.set(key, {
          key, name, pendingQty: 0, processingQty: 0, totalQty: 0, orderCount: 0, entries: [],
        });
      }
      const group = groups.get(key);
      group.totalQty += quantity;
      if (status === "PENDING") group.pendingQty += quantity;
      else group.processingQty += quantity;
      group.entries.push({
        key: taskKey,
        orderId: job.jobId,
        taskId: task.taskId,
        variantId: task.metadata?.variantId ?? task.itemId,
        jobStatus: job.status,
        token: job.token,
        platform: job.source,
        quantity,
        status,
        created: job.aggregated ? task.created ?? job.created : job.created,
        earliestCreatedAt: task.earliestCreatedAt,
        prepTime: task.aggregationPrepTime ?? job.metadata?.avgPrepTime,
        note: text(task.metadata?.comments),
        addons: normalizeAddons(task.additionalProperties?.addons),
        variant,
        station: text(task.metadata?.prepStationName ?? stationId),
      });
    });
  });
  return [...groups.values()].map((group) => ({
    ...group,
    orderCount: new Set(group.entries.map((entry) => entry.orderId)).size,
  })).sort((a, b) => b.totalQty - a.totalQty || a.name.localeCompare(b.name));
};

export const mapKitchenJob = (job) => ({
  id: job.jobId,
  orderNo: job.jobId,
  token: job.token,
  platform: job.source,
  orderType: job.type,
  prepTime: job.metadata?.avgPrepTime ?? "N/A",
  created: job.created,
  externalRefId: job.externalRefId,
  cartId: job.metadata?.cartId,
  customerName: job.metadata?.userName ?? "",
  slotId: job.slotId,
  qty: job.tasks?.length || 0,
  status: job.status,
  metadata: job.metadata || {},
  items: (job.tasks || []).filter(Boolean).map((task) => ({
    id: task.itemId,
    taskId: task.taskId,
    jobId: job.jobId,
    created: task.created ?? job.created,
    prepTime: task.prepTime ?? task.avgPrepTime,
    prepTimeSeconds: task.prepTimeSeconds,
    stationSteps: task.stationSteps,
    externalId: task.externalId,
    name: task.title,
    qty: task.quantity,
    status: task.status || job.status,
    taskStatus: task.taskStatus,
    note: task.metadata?.comments || "",
    metadata: task.metadata || {},
    addons: task.additionalProperties?.addons || [],
  })),
});
